import VueCookie from 'vue-cookie';
import { ActionTree } from 'vuex';
import { normalize } from 'normalizr';
import { ClassroomModel, ListModel } from '@descomplica/dex-models';
import {
  listSession as listSessionSchema,
  user as userSchema,
} from '@descomplica/dex-store/lib/normalizr_schema';
import { ENDPOINTS } from '@/settings/endpoints';
import { chimeraQuery, dexQuery, dexMutation } from '@/helpers/graphql';
import { UserState } from './state';
import RootState from '@/store/root_state';
import {
  allListSessionsQuery,
  getForumParticipantsInfoQuery,
  userInfoByEmail,
  userInitialProgress,
  userStartedEnrollments,
  syncUserCoursesMutation,
} from '../../queries/dexQueries';
import { chimeraUserInformationQuery } from '../../queries/chimeraQueries';
import Analytics from '@/helpers/analytics';
import SentryClient from '@/helpers/sentry';
import { UndergradCourses } from '@/models/undergrad/undergrad-courses.model';
import { UNDERGRADUATE_CATEGORY_SLUG } from '@/helpers/category';
import { getChimeraToken } from '@/helpers/get-tokens';
import { PostGradCourseModel } from '@/models/post-grad/PostGradCourseModel';

// mutations
const SIGNOUT = 'SIGNOUT';
const SET_USER = 'SET_USER';
const SET_TOKEN = 'SET_TOKEN';

type ChimeraCourse = {
  name: string;
  dexCourseId: number;
  category: {
    name: string;
    slug: string;
  };
};

type ChimeraRole = { name: string };

type ChimeraMe = {
  name: string;
  firstName: string;
  lastName: string;
  pictureSHA: string;
  featureFlags: {
    'redirect-to-classrooms': boolean;
    'social-metaverse': boolean;
    'new-terms-update-dialog': boolean;
    'public-video-notes': boolean;
    'equal-web-accessibility-widget': boolean;
    'undergrad-classrooms-due-date-warning': boolean;
    'grade-activities-via-ai': boolean;
    'verify-microcertificates-eligibility-automatically': boolean;
    'ug-lms-grade-worthy-activities': boolean;
    'should-show-classrooms-ug-onboarding': boolean;
    'ug-lms-study-calendar-google-integration': boolean;
    'ug-lms-onboarding-v-2': boolean;
    'ug-lms-ticket-central': boolean;
    'ug-lms-chat-bot': boolean;
  };
  courses: ChimeraCourse[];
  settings: {
    experiments: {
      migrationToClassroomsEnabled: boolean;
    };
  };
  roles: ChimeraRole[];
};

type DexUser = {
  id: number;
  name: string;
  email: string;
};

type Course = {
  name: string;
  category: string;
};

const identifyUser = (payload: {
  me: ChimeraMe;
  dexUser: DexUser;
  undergradCourses: UndergradCourses[];
}) => {
  const { me, dexUser, undergradCourses } = payload;
  const chimeraCourses: ChimeraCourse[] = me.courses;
  const courses: Course[] = [];

  chimeraCourses.forEach((course) => {
    courses.push({
      name: course.name,
      category: course.category.slug,
    });
  });

  undergradCourses.forEach((course: UndergradCourses) => {
    courses.push({
      name: course.name,
      category: UNDERGRADUATE_CATEGORY_SLUG,
    });
  });

  const categories: string[] = courses.map((course) => course.category);
  const categoriesSet: Set<string> = new Set<string>(categories);
  const uniqueCatogories: string[] = Array.from<string>(categoriesSet);

  const flags = Analytics.flagsBuilder(me.featureFlags);
  Analytics.identify({
    id: dexUser.id,
    name: dexUser.name,
    email: dexUser.email,
    flags,
    courses: courses.map((course: Course) => course.name),
    categories: uniqueCatogories,
  });
};

export const enrollUserInMissingCourses = async () => {
  const descomplicaToken = getChimeraToken();
  await dexMutation(syncUserCoursesMutation, {
    userToken: descomplicaToken,
  });
};

export const getChimeraUserInfo = async (commit: Function): Promise<ChimeraMe | undefined> => {
  const response = await chimeraQuery(chimeraUserInformationQuery);
  const { me }: { me: ChimeraMe } = response.data.chimera;

  if (!me) return undefined;

  commit('SET_SHOULD_DISPLAY_SOCIAL_METAVERSE', me.featureFlags['social-metaverse']);
  commit('SET_CAN_VIEW_TERMS_OF_USE', me.featureFlags['new-terms-update-dialog']);
  commit('SET_SHOULD_DISPLAY_PUBLIC_VIDEO_NOTES', me.featureFlags['public-video-notes']);
  commit('SET_SHOULD_SHOW_EQUAL_WEB_WIDGET', me.featureFlags['equal-web-accessibility-widget']);
  commit('SET_SHOW_DUE_DATE_WARNING', me.featureFlags['undergrad-classrooms-due-date-warning']);
  commit('SET_GRADE_ACTIVITIES_VIA_AI', me.featureFlags['grade-activities-via-ai']);
  commit(
    'SET_VERIFY_MICROCERTIFICATES_ELIGIBILITY_AUTOMATICALLY',
    me.featureFlags['verify-microcertificates-eligibility-automatically'],
  );
  commit(
    'SET_SHOULD_SHOW_GRADE_WORTHY_ACTIVITIES',
    me.featureFlags['ug-lms-grade-worthy-activities'],
  );
  commit(
    'SET_SHOULD_SHOW_CLASSROOMS_UG_ONBOARDING',
    me.featureFlags['should-show-classrooms-ug-onboarding'],
  );
  commit(
    'SET_SHOULD_SHOW_STUDY_CALENDAR_GOOGLE_INTEGRATION',
    me.featureFlags['ug-lms-study-calendar-google-integration'],
  );
  commit('SET_SHOULD_SHOW_ONBOARDING_V2', me.featureFlags['ug-lms-onboarding-v-2']);
  commit('SET_SHOULD_SHOW_TICKET_CENTRAL', me.featureFlags['ug-lms-ticket-central']);
  commit('SET_SHOULD_SHOW_CHAT_BOT', me.featureFlags['ug-lms-chat-bot']);
  commit('SET_CHIMERA_USER_COURSES', me.courses);

  return me;
};

export const dexAuthentication = async (d: string, commit: Function): Promise<any> => {
  const cookieResponse = await fetch(`${ENDPOINTS.dexAuthentication}`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      d,
    }),
  });

  if (cookieResponse.status !== 200) {
    commit(SIGNOUT);
    return undefined;
  }

  return cookieResponse.json();
};

const actions: ActionTree<UserState, RootState> = {
  async VALIDATE({ commit, dispatch }): Promise<boolean> {
    const d = VueCookie.get('d', { domain: '.descomplica.com.br' });
    if (!d) {
      commit(SIGNOUT);
      return false;
    }

    const dexAuthResponse = await dexAuthentication(d, commit);

    if (!dexAuthResponse) {
      // we are now sending the error to Sentry using the api method capture error
      // https://docs.sentry.io/platforms/javascript/vue/
      SentryClient.captureError(new Error('Dex Auth failed'));

      return false;
    }

    const { token, user } = dexAuthResponse;

    if (user && token) {
      commit(SET_TOKEN, token);
      commit(SET_USER, user);

      await Analytics.registerIfIsInTrial();
      Analytics.identify({
        id: user.id,
        name: user.name,
        email: user.email,
      });
      dispatch('GET_ALUMNI_PROFILE', user);
      dispatch('GET_GDH_DATA', { email: user.email });
      return true;
    }

    commit(SIGNOUT);
    return false;
  },

  async PULL_USER_INFO({ commit, state, dispatch, getters }): Promise<void> {
    const { user } = state;
    if (!user) return;

    await enrollUserInMissingCourses();

    const [response, me] = await Promise.all([
      dexQuery(userInfoByEmail, { userEmail: user.email }),
      getChimeraUserInfo(commit),
    ]);
    if (!me) return;

    const dexUser = response.data.userByEmail;
    const posCourses: PostGradCourseModel[] = response.data.userByEmail.contractsByUserIdList.map(
      (c: PostGradCourseModel) => c.courseByCourseId,
    );

    const normalizedResponse = normalize(response.data, {
      userByEmail: userSchema,
    });

    if (!normalizedResponse.entities.users) return;

    const userNormalized = normalizedResponse.entities.users[user.id];

    commit('SET_USER', userNormalized);
    commit('SET_USERS', normalizedResponse.entities);
    commit('SET_POS_COURSES', posCourses);

    await dispatch('PULL_USER_UNDERGRAD_COURSES');

    const { undergradCourses } = getters;

    if (dexUser) {
      identifyUser({
        me,
        dexUser,
        undergradCourses,
      });
    }
  },

  async PULL_USER_ENROLLMENTS({ commit, state }, payload): Promise<void> {
    const { user } = state;
    const { courseId } = payload;

    if (!user) return;

    const response = await dexQuery(userStartedEnrollments, {
      userEmail: user.email,
      currentCourseId: courseId,
    });

    const normalizedResponse = normalize(response.data, {
      userByEmail: userSchema,
    });

    if (!normalizedResponse.entities.users) return;

    const userEnrollments = normalizedResponse.entities.users[user.id];

    commit('SET_USER', { ...user, ...userEnrollments });
    commit('SET_USERS', normalizedResponse.entities);
    commit('SET_BOARDS', normalizedResponse.entities);
    commit('SET_BOARDS_CLASSROOMS', normalizedResponse.entities);
    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_ENROLLMENTS', normalizedResponse.entities);
    commit('SET_KNOWLEDGE_ASSOCIATIONS', normalizedResponse.entities);
    commit('SET_KNOWLEDGES', normalizedResponse.entities);
    commit('SET_LIST_ITEMS', normalizedResponse.entities);
    commit('SET_LISTS', normalizedResponse.entities);
    commit('SET_PROVIDER_ASSOCIATIONS', normalizedResponse.entities);
    commit('SET_QUESTIONS', normalizedResponse.entities);
    commit('SET_BOARDS_DEGREES', normalizedResponse.entities);
    commit('SET_DEGREES', normalizedResponse.entities);
    commit('SET_PROGRESSES', normalizedResponse.entities);
    commit('SET_ROLES', normalizedResponse.entities);
    commit('SET_STAFFS', normalizedResponse.entities);
  },

  async PULL_INITIAL_PROGRESS_AND_LISTS({ commit, state, dispatch }, payload): Promise<void> {
    const { user } = state;
    const { courseId } = payload;

    if (!user) return;

    commit('SET_LOADING_USER_BASE_PROGRESS', { isLoading: true });

    const response = await dexQuery(userInitialProgress, {
      userEmail: user.email,
      currentCourseId: courseId,
    });

    const normalizedResponse = normalize(response.data, {
      userByEmail: userSchema,
    });

    if (!normalizedResponse.entities.users) return;

    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_PROGRESSES', normalizedResponse.entities);
    commit('SET_USERS', normalizedResponse.entities);
    commit('SET_ASSERTIONS', normalizedResponse.entities);
    commit('SET_DEGREES', normalizedResponse.entities);
    commit('SET_LISTS', normalizedResponse.entities);
    commit('SET_LOADING_USER_BASE_PROGRESS', { isLoading: false });

    const { classrooms } = normalizedResponse.entities;

    if (classrooms) {
      const classroomIds = Object.keys(classrooms).map((classroomId: string) =>
        Number(classroomId),
      );
      dispatch('PULL_CLASSROOMS_PROGRESS', { classroomIds });
    }

    const { lists } = normalizedResponse.entities;
    if (lists) {
      const listIds = Object.keys(lists).map((listId: string) => Number(listId));
      dispatch('PULL_LISTS_PROGRESS', { listIds });
    }
  },

  async PULL_USER_LISTS({ commit }, payload): Promise<void> {
    const { userId, classrooms } = payload;
    const listIds = classrooms.reduce((acc: Array<number>, classroom: ClassroomModel) => {
      return [
        ...acc,
        ...classroom
          .getLists()
          .map((list: ListModel) => list.id)
          .filter((listId: number) => listId),
      ];
    }, []);

    if (!userId || !listIds || listIds.length === 0) return;
    const response = await dexQuery(allListSessionsQuery, { userId, listIds });

    const normalizedResponse = normalize(response.data.allListSessions, {
      nodes: [listSessionSchema],
    });

    commit('SET_LIST_SESSIONS', normalizedResponse.entities);
    commit('UPDATE_LISTS_WITH_SESSIONS', normalizedResponse.entities);
  },

  async PULL_PARTICIPANTS_INFO({ commit }, { userIdList }: { userIdList: number[] }) {
    if (!userIdList.length) return;

    const response = await dexQuery(getForumParticipantsInfoQuery, {
      userIdList,
    });

    const normalizedResponse = normalize(response.data.allUsers, {
      nodes: [userSchema],
    });

    commit('SET_USERS', normalizedResponse.entities);
  },
};

export default actions;
